import { DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Segmented, Select, Space, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';
import {
  appendExpressionToken,
  buildBubbleAvailableExpression,
  buildFirstAvailableExpression,
  extractMetricIdsFromExpression,
  getAvailableExpressionMetrics,
  getBubbleAvailableMetricId,
  getBubbleAvailableMetrics,
  getExpressionMode,
  getFirstAvailableMetrics,
  removeExpressionTokenAt,
  tokenizeMetricExpression,
  validateMetricExpression,
} from '../utils/metric-expression';

const OPERATOR_OPTIONS = ['+', '-', '*', '/', '(', ')'];
const EXPRESSION_MODE_OPTIONS = [
  { label: '算术表达式', value: 'arithmetic' },
  { label: '优先可用', value: 'first_available' },
  { label: '上级可用', value: 'bubble_available' },
];

const withRenderKeys = (tokens) => {
  const counts = new Map();
  return tokens.map((token, index) => {
    const countKey = `${token.type}-${token.text}`;
    const count = (counts.get(countKey) || 0) + 1;
    counts.set(countKey, count);
    return {
      ...token,
      index,
      renderKey: `${countKey}-${count}`,
    };
  });
};

const MetricExpressionEditor = ({
  metric = {},
  metrics = [],
  onChange,
  operatorOptions = OPERATOR_OPTIONS,
  value,
}) => {
  const mode = getExpressionMode(value);
  const expressionModeOptions =
    metric.valueType === 'ratio'
      ? EXPRESSION_MODE_OPTIONS
      : EXPRESSION_MODE_OPTIONS.filter(
          ({ value: optionValue }) => optionValue !== 'bubble_available',
        );
  const arithmeticMetrics = getAvailableExpressionMetrics(metrics, metric);
  const bubbleAvailableMetrics = getBubbleAvailableMetrics(metrics, metric);
  const firstAvailableMetrics = getFirstAvailableMetrics(metrics, metric);
  const bubbleMetricId =
    mode === 'bubble_available' ? getBubbleAvailableMetricId(value, metrics) : null;
  const candidateIds =
    mode === 'first_available' ? extractMetricIdsFromExpression(value, metrics) : [];
  const metricById = new Map(metrics.map((item) => [item.id, item]));
  const tokens = withRenderKeys(tokenizeMetricExpression(value, metrics));
  const appendToken = (token) => onChange?.(appendExpressionToken(value, token));
  const removeToken = (tokenIndex) => {
    onChange?.(removeExpressionTokenAt(value, tokenIndex, metrics));
  };
  const metricOptions = arithmeticMetrics.map((item) => ({
    label: item.name,
    value: item.id,
  }));
  const candidateOptions = firstAvailableMetrics
    .filter((item) => !candidateIds.includes(item.id))
    .map((item) => ({ label: item.name, value: item.id }));
  const bubbleMetricOptions = bubbleAvailableMetrics.map((item) => ({
    label: item.name,
    value: item.id,
  }));
  const updateCandidates = (nextCandidateIds) => {
    onChange?.(buildFirstAvailableExpression(nextCandidateIds));
  };
  const moveCandidate = (candidateIndex, offset) => {
    const nextCandidateIds = [...candidateIds];
    const nextIndex = candidateIndex + offset;
    [nextCandidateIds[candidateIndex], nextCandidateIds[nextIndex]] = [
      nextCandidateIds[nextIndex],
      nextCandidateIds[candidateIndex],
    ];
    updateCandidates(nextCandidateIds);
  };

  return (
    <Flex vertical gap="middle">
      <Flex vertical gap="small">
        <Typography.Text>表达式模式</Typography.Text>
        <Segmented
          name="expression-mode"
          options={expressionModeOptions}
          value={mode}
          onChange={(nextMode) => {
            if (nextMode === 'first_available') {
              onChange?.(buildFirstAvailableExpression());
              return;
            }
            onChange?.(nextMode === 'bubble_available' ? buildBubbleAvailableExpression() : '');
          }}
        />
      </Flex>
      {mode === 'arithmetic' ? (
        <>
          <Flex vertical gap="small">
            <Typography.Text>计算表达式</Typography.Text>
            <Space wrap size={[4, 8]}>
              {tokens.length === 0 && (
                <Typography.Text type="secondary">请选择指标和运算符号</Typography.Text>
              )}
              {tokens.map((token) => (
                <Tag
                  key={token.renderKey}
                  color={token.type === 'metric' ? 'geekblue' : 'default'}
                  closable
                  onClose={(event) => {
                    event.preventDefault();
                    removeToken(token.index);
                  }}
                >
                  {token.label || token.text}
                </Tag>
              ))}
            </Space>
          </Flex>
          <Flex vertical gap="small">
            <Typography.Text>指标列表</Typography.Text>
            <Select
              aria-label="指标"
              options={metricOptions}
              placeholder="请选择指标"
              showSearch={{ optionFilterProp: 'label' }}
              value={null}
              onChange={appendToken}
            />
          </Flex>
          <Flex vertical gap="small">
            <Typography.Text>运算符号</Typography.Text>
            <Space wrap size={[4, 8]}>
              {operatorOptions.map((operator) => (
                <Button key={operator} size="small" onClick={() => appendToken(operator)}>
                  {operator}
                </Button>
              ))}
              <Button size="small" onClick={() => onChange?.('')}>
                清空
              </Button>
            </Space>
          </Flex>
        </>
      ) : null}
      {mode === 'first_available' ? (
        <Flex vertical gap="middle">
          <Flex vertical gap="small">
            <Typography.Text>候选指标（按优先级排序）</Typography.Text>
            {candidateIds.length === 0 ? (
              <Typography.Text type="secondary">请至少添加两个候选指标</Typography.Text>
            ) : null}
            {candidateIds.map((candidateId, index) => {
              const candidateName = metricById.get(candidateId)?.name || candidateId;
              return (
                <Flex key={candidateId} align="center" gap="small">
                  <Typography.Text>{index + 1}.</Typography.Text>
                  <Tag color="geekblue">{candidateName}</Tag>
                  <Button
                    aria-label={`上移${candidateName}`}
                    disabled={index === 0}
                    icon={<UpOutlined />}
                    size="small"
                    onClick={() => moveCandidate(index, -1)}
                  />
                  <Button
                    aria-label={`下移${candidateName}`}
                    disabled={index === candidateIds.length - 1}
                    icon={<DownOutlined />}
                    size="small"
                    onClick={() => moveCandidate(index, 1)}
                  />
                  <Button
                    aria-label={`移除${candidateName}`}
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() =>
                      updateCandidates(candidateIds.filter((_, itemIndex) => itemIndex !== index))
                    }
                  />
                </Flex>
              );
            })}
          </Flex>
          <Flex vertical gap="small">
            <Typography.Text>添加候选指标</Typography.Text>
            <Select
              aria-label="候选指标"
              options={candidateOptions}
              placeholder="请选择候选指标"
              showSearch={{ optionFilterProp: 'label' }}
              value={null}
              onChange={(candidateId) => updateCandidates([...candidateIds, candidateId])}
            />
          </Flex>
        </Flex>
      ) : null}
      {mode === 'bubble_available' ? (
        <Flex vertical gap="small">
          <Typography.Text>上级原子比率指标</Typography.Text>
          <Select
            allowClear
            aria-label="上级指标"
            options={bubbleMetricOptions}
            placeholder="请选择上级指标"
            showSearch={{ optionFilterProp: 'label' }}
            value={bubbleMetricId}
            onChange={(metricId) => onChange?.(buildBubbleAvailableExpression(metricId))}
          />
        </Flex>
      ) : null}
    </Flex>
  );
};

MetricExpressionEditor.propTypes = {
  metric: PropTypes.shape({
    id: PropTypes.string,
    enabled: PropTypes.bool,
    metricType: PropTypes.string,
    metricId: PropTypes.string,
    valueType: PropTypes.string,
  }),
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool,
      id: PropTypes.string,
      metricType: PropTypes.string,
      name: PropTypes.string,
      valueType: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  operatorOptions: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
};

const MetricExpressionFormItem = ({ metric, metrics = [] }) => {
  return (
    <>
      <Form.Item label="计算时机" name="calculationMode" rules={[{ required: true }]}>
        <Segmented
          options={[
            { label: '公式后置', value: 'post_aggregate' },
            { label: '公式前置', value: 'pre_aggregate' },
          ]}
        />
      </Form.Item>
      <Form.Item
        name="expression"
        rules={[
          {
            validator: (_, value) => {
              const error = validateMetricExpression(value, metrics, metric);
              return error ? Promise.reject(new Error(error)) : Promise.resolve();
            },
          },
        ]}
      >
        <MetricExpressionEditor metric={metric} metrics={metrics} />
      </Form.Item>
    </>
  );
};

MetricExpressionFormItem.propTypes = {
  metric: PropTypes.shape({
    metricId: PropTypes.string,
    valueType: PropTypes.string,
  }).isRequired,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      valueType: PropTypes.string,
    }),
  ),
};

export { MetricExpressionEditor, MetricExpressionFormItem };
